/**
 * fetchGlbBytesCached — cache-first fetch of a GLB's raw bytes, with the loud
 * guards every GLB consumer needs (empty body, SPA-HTML fallback). Returns the
 * ArrayBuffer; the caller applies its own parse (a full GLTFLoader load for
 * rendering, or a scene-extras read for provenance).
 *
 * Shared by AvatarModel's boot loader and readGeneratedMotionExtras — the two
 * used to duplicate this fetch/guard/store block verbatim.
 *
 * Reads/writes the animCache (the Cache-Storage bucket keyed url+version) so a
 * warm reload skips the multi-MB download. Best-effort: a cache read failure
 * falls through to the network; a cache write never throws. Loud on real
 * failures — a bad status / empty / HTML response THROWS (CLAUDE.md: no silent
 * fallbacks on server data).
 */
import { animCache } from './animCache.js'
import { glbTruncationReason } from './glbIntegrity.js'

/**
 * @param {string} url the GLB's file_url
 * @param {string|number|null} [version] cache version token (updatedAt / id);
 *   must match the writer's key to hit the warm entry
 * @returns {Promise<ArrayBuffer>} the GLB bytes (from cache or network)
 */
export async function fetchGlbBytesCached(url, version = null) {
  if (!url) throw new Error('fetchGlbBytesCached: no file_url to fetch')
  try {
    const cached = await animCache.matchArrayBuffer(url, version)
    if (cached) return cached
  } catch { /* cache miss/error — fall through to the network */ }

  // BYPASS the browser's HTTP cache on this network fetch. This line only runs
  // when the Cache-Storage lookup above MISSED, and the key is url+version — so
  // the most important reason to be here is that the server copy CHANGED (a
  // re-upload bumped `version`). The HTTP cache knows nothing of that: it keys
  // on the URL alone, and the AAM serves these files `max-age=86400` with no
  // ETag, so a default-mode fetch here reads YESTERDAY'S bytes and stores them
  // under the NEW version key — stale data re-blessed with a fresh label, which
  // no reload and no "Clear Cache" can dislodge (each clear just re-poisons from
  // the HTTP cache). Field case: myra's replaced rig kept loading one-eyed for a
  // day per browser. `reload` fetches from the network and REPLACES the HTTP
  // cache entry; the cost — a full download on every app-cache miss — lands
  // exactly when fresh bytes are the point. blob:/data: URLs keep the plain
  // call: `cache` is meaningless off HTTP, and local previews use blob rigs.
  // The guard is NOT-blob rather than IS-http on purpose: the rig's file_url
  // is a RELATIVE proxy path (/api/character-assets/...), and an is-http test
  // skipped the bypass for exactly the asset this exists for - the stale rig
  // kept coming out of the HTTP cache while the unit test, written with an
  // absolute URL, stayed green.
  const res = /^(blob|data):/i.test(url) ? await fetch(url) : await fetch(url, { cache: 'reload' })
  if (!res.ok) throw new Error(`fetch glb ${res.status} ${res.statusText} for ${url}`)
  const buf = await res.arrayBuffer()
  // Empty body: guard before indexing byte 0 (else `new Uint8Array(buf,0,1)`
  // throws an opaque RangeError instead of a useful message).
  if (buf.byteLength === 0) {
    throw new Error(`expected a GLB at ${url} but the server returned an empty (0-byte) response — the file is missing or the proxy returned nothing`)
  }
  // Loud failure on the SPA HTML fallback ('<') instead of caching garbage.
  if (new Uint8Array(buf, 0, 1)[0] === 0x3c) {
    throw new Error(`expected a GLB at ${url} but the server returned HTML — the file is missing or the URL is wrong`)
  }
  // A GLB shorter than its own header declares is an interrupted download, not
  // an asset. Never cache it: this bucket is cache-first, so the broken bytes
  // would be replayed on every later load and a reload would appear to change
  // nothing (the gass incident — see glbIntegrity.js). Throwing also replaces
  // GLTFLoader's opaque `RangeError: Invalid typed array length` with the file
  // and the shortfall. Non-GLB bodies (tracks JSON) return null and fall through.
  const truncated = glbTruncationReason(buf)
  if (truncated) {
    throw new Error(`${truncated} — download of ${url} did not complete. Not cached; the next load re-fetches it.`)
  }
  // Copy bytes into the cache now (synchronous Blob copy) — safe even though a
  // caller's parse may transfer `buf` to the Draco worker and detach it.
  animCache.store(url, version, new Blob([buf])) // best-effort; never throws
  return buf
}
